import _ from 'lodash';

export class QueryContext {
  constructor(client, builderInstance) {
    this.client = client;
    this._builder = builderInstance;
    this._key = null;
    this._data = null;
    this._error = null;
  }

  async exec() {
    const built = this._builder.build();
    const queryStr = this._builder.toGraphQLString();
    const storeKey = this.client.store.getPrefix(built.name);
    const storeObj = this.client.store.get(storeKey);

    if (storeObj && !built.options.preserve) {
      storeObj.loading = true;
      this.client.store.emit(storeKey, { ...storeObj });
    }

    try {
      const response = await this.client.api.executeGraphQL(queryStr);
      let result = _.get(response, ['data', 'data', built.alias || built.name]) || [];
      
      if (_.isFunction(built.options.map)) {
        result = result.map(built.options.map);
      }

      this._data = result;
      this._error = null;
    } catch (error) {
      this._error = error;
      this._data = null;
    }
    
    return this;
  }

  save(...args) {
    const built = this._builder.build();
    const storeKey = this.client.store.getPrefix(built.name);
    
    if (!this.client.store.get(storeKey)) {
      this.client.store.save(storeKey, {});
    }
    
    const storeObj = this.client.store.get(storeKey);

    if (args.length === 0) {
      this.client.store.save(storeKey, { data: this._data });
    } else {
      for (let i = 0; i < args.length; i += 2) {
        const source = args[i];
        const path = args[i + 1];
        const value = _.isArray(this._data)
          ? this._data.map(record => _.get(record, source))
          : _.get(this._data, source);
        _.set(storeObj, path, value);
      }
      this.client.store.emit(storeKey, storeObj);
    }
    
    return this;
  }

  prefix(name) {
    const built = this._builder.build();
    this.client.store.setPrefix(built.name, name);
    return this;
  }

  key(keyName) {
    this._key = keyName;
    this.client._queries.set(keyName, { builder: this._builder });
    return this;
  }

  async refetch(options = { force: true }) {
    if (!this._key) return Promise.resolve(this);
    
    const queryInfo = this.client._queries.get(this._key);
    if (!queryInfo) return Promise.resolve(this);

    try {
      const builderToUse = this._createRefetchBuilder(queryInfo, options);
      const built = builderToUse.build();
      const storeKey = this._key;
      const storeObj = this.client.store.get(storeKey);

      if (storeObj && (!built.options.preserve || options.force)) {
        storeObj.loading = true;
        this.client.store.emit(storeKey, { ...storeObj });
      }

      const result = await this.client.query(builderToUse).exec();
      this._updateStoreAfterRefetch(result, storeKey, built, options);
      this._handleRefetchCallbacks(result, options);
    } catch (error) {
      this._handleRefetchError(error, options);
    }
    
    return this;
  }

  _createRefetchBuilder(queryInfo, options) {
    if (!_.some([options.lang, options.params, options.filter])) {
      return queryInfo.builder;
    }

    const originalQuery = queryInfo.builder.query;
    const builderToUse = new (queryInfo.builder.constructor)(originalQuery.name);

    builderToUse.query = {
      ...originalQuery,
      fields: [...originalQuery.fields],
      options: { ...originalQuery.options }
    };

    if (options.filter) {
      builderToUse.query.options.filter = {
        ...builderToUse.query.options.filter,
        ...options.filter
      };
    }

    this.client._applyConfiguredFilters(builderToUse, options);

    if (options.params) {
      Object.assign(builderToUse.query.options, options.params);
    }

    return builderToUse;
  }

  _updateStoreAfterRefetch(result, storeKey, built, options) {
    const currentStoreObj = this.client.store.get(storeKey);
    const hasChanged = !_.isEqual(result._data, _.get(currentStoreObj, 'data'));
    
    if (hasChanged || !built.options.preserve || options.force) {
      if (currentStoreObj) {
        _.assign(currentStoreObj, {
          data: result._data,
          error: result._error,
          loading: false
        });
        this.client.store.emit(storeKey, currentStoreObj);
      }
    }
  }

  _handleRefetchError(error, options) {
    if (this._key) {
      const currentStoreObj = this.client.store.get(this._key);
      if (currentStoreObj) {
        _.assign(currentStoreObj, { error, loading: false });
        this.client.store.emit(this._key, currentStoreObj);
      }
    }
    
    if (options.onError) options.onError(error);
    if (options.onFinally) options.onFinally();
  }

  _handleRefetchCallbacks(result, options) {
    if (result._error && options.onError) {
      options.onError(result._error);
    } else if (options.onSuccess) {
      options.onSuccess(result._data);
    }
    
    if (options.onFinally) options.onFinally();
  }

  use(reactSetState) {
    const built = this._builder.build();
    const storeKey = this._key || this.client.store.getPrefix(built.name);

    const { storeObj, activeWatchers } = this.client.store.createStoreObject(
      storeKey,
      this._data,
      this._error
    );

    storeObj.refetch = (opts) => this.refetch(opts);

    if (_.isFunction(reactSetState)) {
      const unsubscribe = storeObj.subscribe(reactSetState);
      activeWatchers.add(unsubscribe);
    }

    this.client.store.save(storeKey, storeObj);
    this.client.store.emit(storeKey, storeObj);
    
    return storeObj;
  }
}



// WEBPACK FOOTER //
// ./src/app/lib/directus-sdk/query.js